// src/components/NotificationsMenu.jsx

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Snackbar,
} from '@mui/material';
import {
    Notifications,
    ClearAll,
    Close,
    ErrorOutline,
    WarningAmberOutlined,
    CheckCircleOutline,
    InfoOutlined,
} from '@mui/icons-material';
import styled from 'styled-components';
import tw from 'twin.macro';
import PropTypes from 'prop-types';
import api from '../utils/axiosConfig';
import { getSocket } from '../services/socketService';
import { getSelectedCicloEscolarId } from '../utils/schoolContext';
import { shouldShowToast } from './notificationToastRules';

// Botón estilizado (twin.macro + styled-components)
const NotificationIconButton = styled(IconButton)`
    ${tw`text-gray-700`}
    &:hover {
        ${tw`text-gray-900`}
    }
`;

// Card del popup de notificación en tiempo real: look propio (pill + acento + icono
// circular) para que se distinga a simple vista de los Snackbar/Alert genéricos del resto
// del sitio.
const ToastCard = styled.div`
    ${tw`flex items-start gap-3 bg-white rounded-2xl p-4`}
    width: 380px;
    max-width: calc(100vw - 32px);
    box-shadow: 0 12px 32px -8px rgba(17, 24, 39, 0.25), 0 0 0 1px rgba(17, 24, 39, 0.04);
    border: 2px solid ${(props) => props.$accent};
    cursor: pointer;
`;

const ToastIconBadge = styled.div`
    ${tw`flex items-center justify-center rounded-full flex-shrink-0`}
    width: 38px;
    height: 38px;
    background: ${(props) => props.$iconBg};
    color: ${(props) => props.$accent};
`;

const ToastCloseButton = styled(IconButton)`
    ${tw`text-gray-400`}
    padding: 2px !important;
    margin: -4px -4px 0 0 !important;
    &:hover {
        ${tw`text-gray-700`}
    }
`;

const TOAST_KIND_STYLES = {
    error: { accent: '#e53935', iconBg: 'rgba(229, 57, 53, 0.12)', icon: ErrorOutline },
    warning: { accent: '#fb8c00', iconBg: 'rgba(251, 140, 0, 0.12)', icon: WarningAmberOutlined },
    success: { accent: '#00a854', iconBg: 'rgba(0, 168, 84, 0.12)', icon: CheckCircleOutline },
    info: { accent: '#6c5ce7', iconBg: 'rgba(108, 92, 231, 0.12)', icon: InfoOutlined },
};

const getToastKind = (notification) => {
    switch (notification?.title) {
        case 'Emergencia Reportada':
        case '⚠️ Reporte Mecánico Urgente':
            return 'error';
        case 'Incidente Reportado':
        case 'Bus en Taller':
        case '🔧 Nuevo Reporte Mecánico':
        case 'Ruta con retraso':
        case 'Asistencia sin marcar':
        case 'Recorrido sin iniciar':
        case 'Recorrido iniciado tarde':
        case 'Recorrido sin finalizar':
            return 'warning';
        case 'Pago Confirmado':
        case 'Registro de Asistencia':
        case 'Ruta Finalizada':
            return 'success';
        default:
            return 'info';
    }
};

const NotificationsMenu = ({ authToken }) => {
    const { auth } = useContext(AuthContext);
    const currentRoleId = auth?.user?.roleId;

    const resolveNotificationCycle = (source = {}) => ({
        cicloEscolarId: source?.payment?.cicloEscolarId || source?.receipt?.cicloEscolarId || source?.metadata?.client?.cicloEscolarId || source?.school?.cicloEscolarId || getSelectedCicloEscolarId() || ''
    });

    // Helper: ensure links to SchoolPaymentsPage use the cycle tied to the notification/payment.
    const normalizeCycleInLink = (rawLink, source = {}) => {
        if (!rawLink || typeof rawLink !== 'string') return rawLink;
        const { cicloEscolarId } = resolveNotificationCycle(source);
        try {
            const base = window.location.origin;
            const u = new URL(rawLink, base);

            if (cicloEscolarId) {
                const legacyMatch = u.pathname.match(/^\/admin\/escuelas\/\d{4}\/(\d+)(\/.*)?$/);
                const cycleMatch = u.pathname.match(/^\/admin\/escuelas\/ciclo\/[^/]+\/(\d+)(\/.*)?$/);

                if (legacyMatch) {
                    u.pathname = `/admin/escuelas/ciclo/${cicloEscolarId}/${legacyMatch[1]}${legacyMatch[2] || ''}`;
                } else if (cycleMatch) {
                    u.pathname = `/admin/escuelas/ciclo/${cicloEscolarId}/${cycleMatch[1]}${cycleMatch[2] || ''}`;
                }

                u.searchParams.set('cicloEscolarId', String(cicloEscolarId));
            }
            u.searchParams.delete('school' + 'Year');
            return u.pathname + u.search;
        } catch (e) {
            // Best-effort string replace
            try {
                if (!cicloEscolarId) return rawLink;
                return rawLink.replace(/\/admin\/escuelas\/\d{4}\/(\d+)(\/[^?]*)?/, `/admin/escuelas/ciclo/${cicloEscolarId}/$1$2`);
            } catch (_) {
                return rawLink;
            }
        }
    };

    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const menuRef = useRef();
    const navigate = useNavigate();
    
    // Lazy loading state
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [totalNotifications, setTotalNotifications] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const scrollContainerRef = useRef(null);
    const NOTIFICATIONS_PER_PAGE = 10;

    // ==============================
    // 1) Obtener conteo de notificaciones no leídas (optimizado)
    // ==============================
    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread-count', {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setUnreadCount(response.data.unreadCount || 0);
        } catch (err) {
            console.error('Error fetching unread count:', err);
            setUnreadCount(0);
        }
    }, [authToken]);

    // ==============================
    // 2) Cargar notificaciones con paginación
    // ==============================
    const fetchNotifications = useCallback(async (pageNum = 0, append = false) => {
        if (loading) return;
        
        try {
            setLoading(true);
            const offset = pageNum * NOTIFICATIONS_PER_PAGE;
            const response = await api.get(`/notifications?limit=${NOTIFICATIONS_PER_PAGE}&offset=${offset}`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            
            const fetched = Array.isArray(response.data.notifications)
                ? response.data.notifications
                : [];
            const total = response.data.total || 0;

            // Normalize targetingCriteria if it's returned as a string
            const normalized = fetched.map((n) => {
                const copy = { ...n };
                try {
                    if (copy.targetingCriteria && typeof copy.targetingCriteria === 'string') {
                        copy.targetingCriteria = JSON.parse(copy.targetingCriteria);
                    }
                } catch (e) {
                    console.warn('Failed parsing targetingCriteria for notification', copy.id, e);
                }
                return copy;
            });

            setTotalNotifications(total);
            
            if (append) {
                setNotifications(prev => [...prev, ...normalized]);
            } else {
                setNotifications(normalized);
            }
            
            // Check if there are more notifications to load
            setHasMore(offset + normalized.length < total);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            if (!append) {
                setNotifications([]);
            }
        } finally {
            setLoading(false);
        }
    }, [authToken, loading]);

    // ==============================
    // 3) Marcar una notificación como leída
    // ==============================
    const markNotificationAsRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`, null, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            // En local, cambiamos su estado a "read"
            setNotifications((prev) =>
                prev.map((n) =>
                    n.id === notificationId
                        ? { ...n, status: 'read' }
                        : n
                )
            );
            // Actualizar contador
            await fetchUnreadCount();
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    // Helper para ocultar localmente (remover) una notificación
    const hideNotificationLocal = (notificationId) => {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    };

    const handleNotificationClick = async (notification) => {
        // Do NOT auto-mark notification as read when user clicks the card.
        // Only the X (delete) will mark as read. This preserves the user's
        // explicit action intent.
        // For 'Nueva Boleta de Pago', show the receipt preview inside the menu (no navigation).
        if (notification && notification.type === 'boleta-pago') {
            setAnchorEl(null);
            // Fetch detailed preview on demand (receipt + payment)
            try {
                const res = await api.get(`/notifications/${notification.id}/preview`, { headers: { Authorization: `Bearer ${authToken}` } });
                const preview = res.data && res.data.preview ? res.data.preview : { notification, receipt: null };
                setPreviewNotification(preview);
                setPreviewOpen(true);
            } catch (e) {
                const previewData = { notification, receipt: null };
                setPreviewNotification(previewData);
                setPreviewOpen(true);
            }
            return;
        }

        // Otherwise, if notification has a link, navigate to it
        if (notification && notification.link) {
            try {
                setAnchorEl(null);
                const { cicloEscolarId } = resolveNotificationCycle(notification);
                const stateObj = notification.payment || cicloEscolarId ? { ...(notification.payment ? { payment: notification.payment } : {}), ...(cicloEscolarId ? { cicloEscolarId } : {}) } : undefined;
                const linkToOpen = normalizeCycleInLink(notification.link, notification);
                navigate(linkToOpen, { state: stateObj });
            } catch (e) {
                console.error('Error navigating to notification link', e);
            }
        }
    };

    // Preview modal state
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewNotification, setPreviewNotification] = useState(null); // { notification, receipt }

    // Toast (popup) state: cola de notificaciones nuevas por socket, se muestran de a una
    const [toastQueue, setToastQueue] = useState([]);
    const [currentToast, setCurrentToast] = useState(null);

    const handleToastClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setCurrentToast(null);
    };

    const handleToastClick = () => {
        if (currentToast) {
            // Solo navega/abre preview: marcar como leída exige el botón "Marcar como leída".
            handleNotificationClick(currentToast);
        }
        setCurrentToast(null);
    };

    // X: solo cierra, no marca como leída (leerla exige acción explícita: el
    // botón "Marcar como leída").
    const handleToastDismiss = () => {
        setCurrentToast(null);
    };

    const handleMarkToastAsRead = (e) => {
        e.stopPropagation();
        if (currentToast) {
            markNotificationAsRead(currentToast.id);
        }
        setCurrentToast(null);
    };

    // Sacar el siguiente toast de la cola cuando no hay uno visible
    useEffect(() => {
        if (!currentToast && toastQueue.length > 0) {
            setCurrentToast(toastQueue[0]);
            setToastQueue((prev) => prev.slice(1));
        }
    }, [toastQueue, currentToast]);

    // ==============================
    // 4) Scroll infinito - cargar más notificaciones
    // ==============================
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || loading || !hasMore) return;
        
        const container = scrollContainerRef.current;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        // Si el usuario ha scrolleado cerca del final (80%), cargar más
        if (scrollHeight - scrollTop - clientHeight < 100) {
            setPage(prev => {
                const nextPage = prev + 1;
                fetchNotifications(nextPage, true);
                return nextPage;
            });
        }
    }, [loading, hasMore, fetchNotifications]);

    // ==============================
    // 5) Marcar TODAS como leídas
    // ==============================
    const markAllNotificationsAsRead = async () => {
        try {
            // Filtramos las "unread"
            const unreadList = notifications.filter((n) => n.status === 'unread');
            for (const notif of unreadList) {
                // Las marcamos como read una por una
                await markNotificationAsRead(notif.id);
            }
            // Actualizar contador
            await fetchUnreadCount();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    // ==============================
    // 6) Al abrir el menú => recargar notificaciones desde el inicio
    // ==============================
    const handleMenuOpen = async (event) => {
        setAnchorEl(event.currentTarget);
        // Reset pagination and fetch first page
        setPage(0);
        setNotifications([]);
        await fetchNotifications(0, false);
    };

    // ==============================
    // 7) Cerrar el menú
    // ==============================
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // ==============================
    // 8) "Limpiar" => marcar todas read
    // ==============================
    const handleClearNotifications = async () => {
        await markAllNotificationsAsRead();
        handleMenuClose();
    };

    // ==============================
    // 9) Efecto para cargar contador y escuchar socket
    // ==============================
    useEffect(() => {
        if (!authToken) return;

        // Cargar conteo inicial
        fetchUnreadCount();

        // Escuchar socket
        const socket = getSocket();
        if (socket) {
            socket.on('new_notification', (newNoti) => {
                const copy = { ...newNoti };
                try {
                    if (copy.targetingCriteria && typeof copy.targetingCriteria === 'string') {
                        copy.targetingCriteria = JSON.parse(copy.targetingCriteria);
                    }
                } catch (e) {
                    console.warn('Failed parsing targetingCriteria from socket new_notification', copy.id, e);
                }

                // Agregar al inicio si el menú está abierto
                if (menuOpen) {
                    setNotifications((prev) => [copy, ...prev]);
                    setTotalNotifications(prev => prev + 1);
                }
                // Actualizar contador
                fetchUnreadCount();
                // Encolar popup solo si el rol actual está habilitado para este type
                if (shouldShowToast(currentRoleId, copy)) {
                    setToastQueue((prev) => [...prev, copy]);
                }
            });

            socket.on('notification_deleted', (payload) => {
                try {
                    const uuidsRaw = payload && (payload.uuids || payload.uuid) ? (payload.uuids || payload.uuid) : null;
                    const uuids = Array.isArray(uuidsRaw) ? uuidsRaw : (uuidsRaw ? [uuidsRaw] : []);

                    if (uuids.length > 0 && menuOpen) {
                        setNotifications((prev) => {
                            const next = prev.filter((n) => !uuids.includes(n.uuid));
                            const removed = prev.length - next.length;
                            if (removed > 0) {
                                setTotalNotifications((t) => Math.max(0, (t || 0) - removed));
                            }
                            return next;
                        });
                    }
                } catch (e) {
                    console.warn('Failed handling notification_deleted payload', e);
                }

                // Actualizar contador (por si borró una unread)
                fetchUnreadCount();
            });
        }
        return () => {
            if (socket) {
                socket.off('new_notification');
                socket.off('notification_deleted');
            }
        };
    }, [authToken, fetchUnreadCount, menuOpen, currentRoleId]);

    const getNotificationStyle = (notification) => {
        // Estilo base para todas las notificaciones
        const baseStyle = {
            transition: 'background-color 0.3s ease',
        };

        // Color más tenue si ya fue leída
        const intensity = notification.status === 'unread' ? 1 : 0.4;

        // Estilos según el título
        switch (notification.title) {
            case 'Registro de Asistencia':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(76, 175, 80, ${intensity})`,
                    backgroundColor: `rgba(232, 245, 233, ${intensity * 0.7})`
                };
            case 'Ruta Finalizada':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(33, 150, 243, ${intensity})`,
                    backgroundColor: `rgba(227, 242, 253, ${intensity * 0.7})`
                };
            case 'Incidente Reportado':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(255, 152, 0, ${intensity})`,
                    backgroundColor: `rgba(255, 243, 224, ${intensity * 0.7})`
                };
            case 'Nueva Boleta de Pago':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(156, 39, 176, ${intensity})`,
                    backgroundColor: `rgba(243, 229, 245, ${intensity * 0.7})`
                };
            case 'Pago Confirmado':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(0, 200, 83, ${intensity})`,
                    backgroundColor: `rgba(232, 245, 233, ${intensity * 0.7})`
                };
            case 'Emergencia Reportada':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(244, 67, 54, ${intensity})`,
                    backgroundColor: `rgba(255, 235, 238, ${intensity * 0.7})`
                };
            case 'Bus en Taller':
                return {
                    ...baseStyle,
                    borderLeft: `4px solid rgba(158, 158, 158, ${intensity})`,
                    backgroundColor: `rgba(238, 238, 238, ${intensity * 0.7})`
                };
            default:
                // Estilo por defecto para otros títulos
                return notification.status === 'unread' ? {
                    ...baseStyle,
                    borderLeft: '4px solid rgba(33, 150, 243, 1)',
                    backgroundColor: 'rgba(227, 242, 253, 0.7)'
                } : baseStyle;
        }
    };

    return (
        <>
            <NotificationIconButton onClick={handleMenuOpen} ref={menuRef}>
                <Badge badgeContent={unreadCount} color="secondary">
                    <Notifications />
                </Badge>
            </NotificationIconButton>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{ 
                    sx: {
                        width: { xs: 'calc(100vw - 24px)', sm: 350 },
                        maxWidth: 'calc(100vw - 24px)',
                        maxHeight: { xs: 'calc(100dvh - 88px)', sm: 'calc(100vh - 120px)' },
                        overflowY: 'auto',
                    },
                    ref: scrollContainerRef,
                    onScroll: handleScroll
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                getContentAnchorEl={null}
            >
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
                    <Typography variant="h6" style={{ flexGrow: 1 }}>
                        Notificaciones
                    </Typography>
                    <IconButton size="small" onClick={handleClearNotifications}>
                        <ClearAll />
                    </IconButton>
                </div>
                <Divider />

                {loading && notifications.length === 0 ? (
                    <MenuItem onClick={handleMenuClose}>
                        <Typography variant="body1" color="textSecondary">
                            Cargando...
                        </Typography>
                    </MenuItem>
                ) : notifications.length === 0 ? (
                    <MenuItem onClick={handleMenuClose}>
                        <Typography variant="body1" color="textSecondary">
                            No hay notificaciones.
                        </Typography>
                    </MenuItem>
                ) : [
                    ...notifications.map((notification, index) => {
                        const clientName = notification.targetingCriteria?.client?.name || null;
                        return (
                            <div key={notification.id || index}>
                                <MenuItem
                                    style={{
                                        position: 'relative',
                                        minHeight: '80px',
                                        alignItems: 'flex-start',
                                        paddingRight: 8,
                                        paddingLeft: 16,
                                        ...getNotificationStyle(notification)
                                    }}
                                >
                                    {/* Close (X) button */}
                                    <IconButton
                                        size="small"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                                await markNotificationAsRead(notification.id);
                                            } catch (err) {
                                                // ignore
                                            }
                                            hideNotificationLocal(notification.id);
                                        }}
                                        style={{ position: 'absolute', right: 4, top: 4, zIndex: 5 }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>

                                    <div
                                        onClick={() => handleNotificationClick(notification)}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            width: '100%',
                                            paddingRight: '28px',
                                            paddingBottom: '28px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {clientName && (
                                            <Chip
                                                label={clientName}
                                                size="small"
                                                color="primary"
                                                variant="filled"
                                                style={{ alignSelf: 'flex-start', marginBottom: 6 }}
                                            />
                                        )}
                                        <Typography
                                            variant="body1"
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                                marginBottom: '4px',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {notification.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {notification.message}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{
                                                position: 'absolute',
                                                right: '8px',
                                                bottom: '8px',
                                            }}
                                        >
                                            {new Date(notification.createdAt).toLocaleString()}
                                        </Typography>
                                    </div>
                                </MenuItem>
                                {index < notifications.length - 1 && <Divider />}
                            </div>
                        );
                    }),
                    ...(loading ? [
                        <MenuItem key="loading-more" disabled>
                            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', width: '100%' }}>
                                Cargando más...
                            </Typography>
                        </MenuItem>
                    ] : []),
                    ...(!loading && hasMore ? [
                        <MenuItem key="scroll-more" disabled>
                            <Typography variant="caption" color="textSecondary" style={{ textAlign: 'center', width: '100%' }}>
                                Scroll para cargar más ({notifications.length} de {totalNotifications})
                            </Typography>
                        </MenuItem>
                    ] : []),
                    ...(!loading && !hasMore && notifications.length > 0 ? [
                        <MenuItem key="total-count" disabled>
                            <Typography variant="caption" color="textSecondary" style={{ textAlign: 'center', width: '100%' }}>
                                {notifications.length} de {totalNotifications} notificaciones
                            </Typography>
                        </MenuItem>
                    ] : [])
                ]}
            </Menu>

            {/* Receipt preview dialog (local) */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Vista previa de Boleta</DialogTitle>
                <DialogContent>
                    {previewNotification && previewNotification.receipt ? (
                        (previewNotification.receipt.fileUrl && (previewNotification.receipt.fileUrl.match(/(\.png|\.jpe?g|\.gif|\.webp|\.bmp)(\?|$)/i))) ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <img src={previewNotification.receipt.fileUrl} alt="boleta" style={{ maxWidth: '100%', maxHeight: '60vh' }} />
                            </div>
                        ) : (
                            <div>
                                {previewNotification.receipt && previewNotification.receipt.fileUrl ? (
                                    <Button variant="outlined" href={previewNotification.receipt.fileUrl} target="_blank" rel="noreferrer">Abrir archivo</Button>
                                ) : (
                                    <Typography variant="body2">No se encontró la boleta para vista previa.</Typography>
                                )}
                            </div>
                        )
                    ) : (
                        <Typography variant="body2">No hay una boleta disponible para vista previa.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
                    <Button variant="contained" onClick={async () => {
                        // when user chooses to register a payment, navigate to page with openRegister=true
                        try {
                            const rec = previewNotification.receipt;
                            
                            
                            // Build URL from notification.link if available, else construct
                            let link = previewNotification.link;
                            
                            // ensure openRegister explicit
                            if (link) {
                                const base = window.location.origin;
                                
                                const u = new URL(link, base);
                                u.searchParams.set('openRegister', 'true');
                                if (previewNotification.receipt.id) u.searchParams.set('receiptId', previewNotification.receipt.id);
                                if (previewNotification.receipt.userId) u.searchParams.set('userId', previewNotification.receipt.userId);
                                
                                const state = previewNotification.paymentReceiptId;

                                setPreviewOpen(false);
                                if (previewNotification.id) {
                                    markNotificationAsRead(previewNotification.id);
                                }
                                const finalPath = normalizeCycleInLink(u.pathname + u.search, previewNotification);
                                navigate(finalPath, { state });
                            }
                        } catch (e) {
                            console.error('Error navigating to register from preview', e);
                        }
                    }}>Registrar Pago</Button>
                </DialogActions>
            </Dialog>

            {/* Popup de notificación nueva (socket): diseño propio para distinguirlo
                de los Snackbar/Alert genéricos de feedback que usa el resto del sitio. */}
            <Snackbar
                open={!!currentToast}
                autoHideDuration={8000}
                onClose={handleToastClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {(() => {
                    const kind = TOAST_KIND_STYLES[getToastKind(currentToast)];
                    const ToastIcon = kind.icon;
                    const toastClientName = currentToast?.targetingCriteria?.client?.name || null;
                    return (
                        <ToastCard $accent={kind.accent} onClick={handleToastClick}>
                            <ToastIconBadge $accent={kind.accent} $iconBg={kind.iconBg}>
                                <ToastIcon fontSize="small" />
                            </ToastIconBadge>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {toastClientName && (
                                    <Chip
                                        label={toastClientName}
                                        size="small"
                                        color="primary"
                                        variant="filled"
                                        style={{ marginBottom: 4 }}
                                    />
                                )}
                                <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: 700, lineHeight: 1.3 }}
                                >
                                    {currentToast?.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ lineHeight: 1.35, marginTop: 2 }}
                                >
                                    {currentToast?.message}
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={handleMarkToastAsRead}
                                    style={{ marginTop: 6, marginLeft: -6, textTransform: 'none' }}
                                >
                                    Marcar como leída
                                </Button>
                            </div>
                            <ToastCloseButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleToastDismiss(); }}
                            >
                                <Close fontSize="small" />
                            </ToastCloseButton>
                        </ToastCard>
                    );
                })()}
            </Snackbar>
        </>
    );
};

NotificationsMenu.propTypes = {
    authToken: PropTypes.string.isRequired,
};

export default NotificationsMenu;
